import styles from './index.module.css'

import React, { useCallback, useEffect, useRef } from 'react'
type Match = {
  index: number
  length: number
}

const getMatches = (
  inputString: string,
  searchTerms: Array<string>,
): Array<Match> => {
  if (
    !inputString ||
    inputString.length == 0 ||
    !searchTerms ||
    searchTerms.length === 0
  )
    return []
  const text = inputString.toLowerCase()
  const terms = searchTerms.map(term => term.toLowerCase())
  const matches: Match[] = []

  for (let i = 0; i < terms.length; i++) {
    const term = terms[i]
    if (!term) continue

    const index = text.indexOf(term)

    if (
      index > -1 &&
      !matches.some(
        match => match.index <= index && index < match.index + match.length,
      )
    ) {
      matches.push({
        index: index,
        length: term.length,
      })
    }
  }

  return matches.sort((a, b) => a.index - b.index)
}

type Props = {
  text: string | null | undefined
  highlights: string[] | null | undefined
  customColors?: {
    background?: string
    text?: string
  }
}

/**
 * A component that highlights specified search terms within text with customizable colors
 * @param text - The text content to search and highlight within
 * @param highlights - Array of search terms to highlight in the text
 * @param customColors - Optional custom colors object with background and text properties
 * @returns A span element with highlighted search terms or plain text if no matches
 */
export const HighlightedText = (props: Props): React.ReactElement<'span'> => {
  const { text, highlights, customColors } = props
  const highlightedTextRef = useRef<HTMLSpanElement>(null)
  const defaultResponse = <span>{text}</span>
  const getCustomColors = useCallback(
    (color?: { background?: string; text?: string }) => {
      const style = highlightedTextRef.current?.style
      if (!color || !style) return
      if (color.background)
        style.setProperty('--background-color', color.background)
      if (color.text) style.setProperty('--text-color', color.text)
    },
    [],
  )
  useEffect(() => {
    getCustomColors(customColors)
  }, [customColors, getCustomColors])
  if (!text || !highlights) return defaultResponse
  const matches = getMatches(text, highlights)
  if (matches.length === 0) return defaultResponse

  const firstMatch = matches[0]
  const lastMatch = matches[matches.length - 1]

  if (!firstMatch || !lastMatch) return defaultResponse

  return (
    <span>
      {text.slice(0, firstMatch.index)}
      {matches.map((match: Match, index: number) => {
        const matchSpan = (
          <span ref={highlightedTextRef} className={styles.HighlightedText}>
            {text.slice(match.index, match.index + match.length)}
          </span>
        )
        const nextMatch = matches[index + 1]
        const nonMatchSpan =
          matches.length - 1 > index && nextMatch
            ? text.slice(match.index + match.length, nextMatch.index)
            : null
        return (
          <span key={`match-${index}`}>
            {matchSpan}
            {nonMatchSpan}
          </span>
        )
      })}

      {text.slice(lastMatch.index + lastMatch.length)}
    </span>
  )
}
